import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { z } from 'zod';

export enum SchedulePlanStatus {
  PLANNING = 'PLANNING',
  EXECUTING = 'EXECUTING',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
}

export enum SchedulePlanAction {
  SCHEDULE_APPOINTMENT = 'SCHEDULE_APPOINTMENT',
  NO_ACTION = 'NO_ACTION',
}

const utcDatetimeSchema = z
  .string()
  .datetime({ offset: true })
  .endsWith('Z', 'Datetime must be in UTC.');

const localDatetimeSchema = z
  .string()
  .datetime({ local: true })
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/,
    'Datetime must be a local ISO 8601 datetime without an offset.'
  );

const isValidFacilityTimezone = (timezone: string) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

const facilityTimezoneSchema = z.string().refine(isValidFacilityTimezone, {
  message: 'Facility timezone must be a valid IANA timezone.',
});

export const resolveSchedulePlanLocalDatetimeToFirstOccurrence = (
  localDatetime: string,
  timezone: string
) => {
  const resolvedDatetime = fromZonedTime(localDatetime, timezone);
  const expectedLocalSecond = localDatetime.slice(0, 19);
  const resolvedLocalSecond = formatInTimeZone(
    resolvedDatetime,
    timezone,
    "yyyy-MM-dd'T'HH:mm:ss"
  );

  if (resolvedLocalSecond !== expectedLocalSecond) {
    throw new RangeError(
      'Local datetime does not exist in the facility timezone.'
    );
  }

  return resolvedDatetime;
};

const isValidLocalDatetime = (localDatetime: string, timezone: string) => {
  try {
    resolveSchedulePlanLocalDatetimeToFirstOccurrence(localDatetime, timezone);
    return true;
  } catch {
    return false;
  }
};

export const SchedulePlanTimeWindowSchema = z
  .object({
    startLocalDatetime: localDatetimeSchema,
    endLocalDatetime: localDatetimeSchema,
  })
  .refine(
    ({ startLocalDatetime, endLocalDatetime }) =>
      startLocalDatetime < endLocalDatetime,
    {
      message: 'Acceptable window start must be before its end.',
    }
  );

const optionalSchedulePlanTimingFields = {
  targetLocalDatetime: localDatetimeSchema.optional(),
  acceptableWindows: z.array(SchedulePlanTimeWindowSchema).min(1).optional(),
};

const ScheduleAppointmentPlanStopSchema = z.object({
  movementStopId: z.string().uuid(),
  sequence: z.number().int().nonnegative(),
  action: z.literal(SchedulePlanAction.SCHEDULE_APPOINTMENT),
  ...optionalSchedulePlanTimingFields,
  earliestOutreachAt: utcDatetimeSchema.optional(),
  facilityTimezone: facilityTimezoneSchema,
});

const NoActionPlanStopSchema = z.object({
  movementStopId: z.string().uuid(),
  sequence: z.number().int().nonnegative(),
  action: z.literal(SchedulePlanAction.NO_ACTION),
  noActionReason: z.string().min(1),
  ...optionalSchedulePlanTimingFields,
  facilityTimezone: facilityTimezoneSchema.optional(),
});

export const SchedulePlanStopSchema = z
  .discriminatedUnion('action', [
    ScheduleAppointmentPlanStopSchema,
    NoActionPlanStopSchema,
  ])
  .superRefine((stop, context) => {
    const { acceptableWindows, facilityTimezone, targetLocalDatetime } = stop;
    const hasAnyTiming =
      acceptableWindows !== undefined || targetLocalDatetime !== undefined;
    if (!hasAnyTiming) return;
    if (!acceptableWindows || !facilityTimezone || !targetLocalDatetime) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Appointment timing must include a target, acceptable windows, and facility timezone.',
      });
      return;
    }

    if (!isValidFacilityTimezone(facilityTimezone)) return;

    const localDatetimes = [
      targetLocalDatetime,
      ...acceptableWindows.flatMap(
        ({ startLocalDatetime, endLocalDatetime }) => [
          startLocalDatetime,
          endLocalDatetime,
        ]
      ),
    ];
    if (
      localDatetimes.some(
        (datetime) => !isValidLocalDatetime(datetime, facilityTimezone)
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Appointment times must exist in the facility timezone.',
      });
    }

    const containsTarget = acceptableWindows.some(
      ({ startLocalDatetime, endLocalDatetime }) =>
        startLocalDatetime <= targetLocalDatetime &&
        targetLocalDatetime <= endLocalDatetime
    );

    if (!containsTarget) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Target datetime must fall within an acceptable window.',
        path: ['targetLocalDatetime'],
      });
    }

    const chronologicalWindows = [...acceptableWindows].sort((a, b) =>
      a.startLocalDatetime.localeCompare(b.startLocalDatetime)
    );
    for (let index = 1; index < chronologicalWindows.length; index += 1) {
      const previousEnd = chronologicalWindows[index - 1].endLocalDatetime;
      const currentStart = chronologicalWindows[index].startLocalDatetime;
      if (currentStart < previousEnd) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Acceptable windows cannot overlap.',
          path: ['acceptableWindows'],
        });
        break;
      }
    }
  });

export const SchedulePlanRouteLegSchema = z.object({
  originMovementStopId: z.string().uuid(),
  destinationMovementStopId: z.string().uuid(),
  mileage: z.number().nonnegative(),
  minimumTransitHours: z.number().nonnegative(),
});

const SchedulePlanStopsSchema = z
  .array(SchedulePlanStopSchema)
  .superRefine((stops, context) => {
    const movementStopIds = new Set<string>();
    const sequences = new Set<number>();

    stops.forEach((stop, index) => {
      if (movementStopIds.has(stop.movementStopId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'A movement stop can appear only once in a schedule plan.',
          path: [index, 'movementStopId'],
        });
      }
      movementStopIds.add(stop.movementStopId);

      if (sequences.has(stop.sequence)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Each schedule plan stop must have a unique sequence.',
          path: [index, 'sequence'],
        });
      }
      sequences.add(stop.sequence);
    });
  });

export const SchedulePlanSchema = z.object({
  route: z.array(SchedulePlanRouteLegSchema).optional(),
  stops: SchedulePlanStopsSchema,
});

export type ScheduleAppointmentPlanStop = z.infer<
  typeof ScheduleAppointmentPlanStopSchema
>;
export type NoActionPlanStop = z.infer<typeof NoActionPlanStopSchema>;
export type SchedulePlanStop = z.infer<typeof SchedulePlanStopSchema>;
export type SchedulePlanRouteLeg = z.infer<typeof SchedulePlanRouteLegSchema>;
export type SchedulePlan = z.infer<typeof SchedulePlanSchema>;
